import React, { useState, useEffect } from 'react';
import { Button, Modal, Input } from '@/components/ui';
import type { ConstructionContract, ContractStatus, ContractType } from '../types/construction-contracts.types';
import { useConstructionContracts } from '../hooks/useConstructionContracts';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useToast } from '@/components/ui/Toast';
import { useUsers } from '@/modules/users/list/hooks/useUserList';
import { toInputDate } from '@/utils/format';
import { contractStatusMap, contractTypeMap } from '@/utils/status';

interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract?: ConstructionContract | null;
  companyId: string;
}

export function ContractFormModal({ isOpen, onClose, contract, companyId }: ContractFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!contract;
  const { createContract, updateContract, isCreating, isUpdating } = useConstructionContracts(companyId);
  const { projects = [] } = useProjects(companyId);
  const { data: users = [] } = useUsers(companyId);

  const [formData, setFormData] = useState<Partial<ConstructionContract>>({
    contract_code: '',
    contract_name: '',
    contractor_name: '',
    project_id: '',
    contract_type: 'unit_price',
    status_code: 'draft',
    contract_value: 0,
    retention_rate: 5,
    advance_payment_amount: 0,
    client_name: '',
    signed_date: '',
    warranty_months: 12,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    pm_user_id: '',
    site_manager_user_id: '',
    qs_user_id: '',
    description: '',
    scope_summary: '',
    payment_terms: '',
  } as any);

  useEffect(() => {
    if (isOpen) {
      if (contract) {
        setFormData({
          ...contract,
          start_date: toInputDate(contract.start_date),
          end_date: toInputDate(contract.end_date)
        });
      } else {
        setFormData({
          contract_code: '',
          contract_name: '',
          contractor_name: '',
          project_id: '',
          contract_type: 'unit_price',
          status_code: 'draft',
          contract_value: 0,
          retention_rate: 5,
          start_date: new Date().toISOString().split('T')[0],
          end_date: '',
          company_id: companyId,
        } as any);
      }
    }
  }, [isOpen, contract, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error('Vui lòng chọn dự án cho hợp đồng');
      return;
    }
    try {
      const { project, pm, site_manager, qs, ...submitData } = formData as any;
      if (isEdit && contract) {
        await updateContract({ id: contract.id, updates: submitData });
      } else {
        await createContract(submitData);
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật hợp đồng thành công' : 'Tạo hợp đồng mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Cập nhật Hợp đồng Thi công' : 'Tạo Hợp đồng Thi công mới'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã hợp đồng *"
            required
            value={formData.contract_code || ''}
            onChange={(e) => setFormData({ ...formData, contract_code: e.target.value })}
            placeholder="VD: CONT-CONSTR-001"
          />
          <Input
            label="Tên hợp đồng *"
            required
            value={formData.contract_name || ''}
            onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
            placeholder="VD: Gói thầu xây lắp hạ tầng"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dự án áp dụng *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <option value="">-- Chọn dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Loại hình hợp đồng *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.contract_type || 'unit_price'}
              onChange={(e) => setFormData({ ...formData, contract_type: e.target.value as ContractType })}
            >
              {Object.entries(contractTypeMap).filter(([val]) => ['lump_sum', 'unit_price', 'cost_plus', 'other'].includes(val)).map(([val, cfg]) => (
                <option key={val} value={val}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || 'draft'}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as ContractStatus })}
            >
              {Object.entries(contractStatusMap).map(([val, cfg]) => (
                <option key={val} value={val}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày bắt đầu *"
            type="date"
            lang="vi"
            required
            value={formData.start_date || ''}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
          />
          <Input
            label="Ngày kết thúc (dự kiến)"
            type="date"
            lang="vi"
            value={formData.end_date || ''}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          />
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Tạo hợp đồng'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
